import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ChevronRight, Trash2, Key, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsScreen = () => {
  const { setCurrentScreen } = useApp();
  const { user, logout } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handlePasswordReset = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email })
      });

      const data = await response.json();
      
      if (response.ok) {
        alert(data.message || 'Password reset email sent!');
      } else {
        alert('Failed to send reset email. Please try again.');
      }
    } catch (error) {
      alert('Error sending reset email. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message || 'Account deleted successfully');
        logout();
        setCurrentScreen('home');
      } else {
        alert(data.detail || 'Failed to delete account. Please try again.');
      }
    } catch (error) {
      alert('Error deleting account. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header with Back Arrow + Large Logo */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-4">
        <button
          onClick={() => setCurrentScreen('profile')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <img 
          src="/logos/logo-black.jpg" 
          alt="ATELIA" 
          className="h-24 w-auto object-contain"
        />
      </div>

      {/* Settings Options */}
      <div className="px-6 space-y-6">
        {/* Password Reset */}
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-3 font-bold">SECURITY</p>
          <button
            onClick={handlePasswordReset}
            className="w-full bg-white border border-black/12 rounded-xl p-4
              flex items-center justify-between hover:bg-black/5 transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center">
                <Key size={18} strokeWidth={1.5} className="text-black" />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-black uppercase">Reset Password</p>
                <p className="text-xs text-black/55 mt-0.5">Send reset email to {user?.email}</p>
              </div>
            </div>
            <ChevronRight size={16} className="text-black/30" />
          </button>
        </div>

        {/* Danger Zone */}
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-3 font-bold">DANGER ZONE</p>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full bg-white border border-black/12 rounded-xl p-4
              flex items-center justify-between hover:bg-red-50 hover:border-red-200 transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <Trash2 size={18} strokeWidth={1.5} className="text-red-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-red-600 uppercase">Delete Account</p>
                <p className="text-xs text-black/55 mt-0.5">Permanently remove your account</p>
              </div>
            </div>
            <ChevronRight size={16} className="text-red-300" />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center px-6"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-sm p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-red-600" />
            </div>
            
            <h2 className="text-xl font-bold text-black text-center mb-2 uppercase">
              Delete Account?
            </h2>
            <p className="text-sm text-black/72 text-center mb-6">
              This action cannot be undone. All your scans, preferences, and data will be permanently deleted.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-white border border-black/12 rounded-xl text-sm font-bold uppercase
                  hover:bg-black/5 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-bold uppercase
                  hover:bg-red-700 transition-all disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsScreen;
